import { Injectable } from "@angular/core";
import { BenefitsFactory, ICard } from "@edp-models/benefits.model";
import { ChallengesService } from "@edp-services/challenges.service";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { EBenefitsActions } from "@edp-store/actions/benefits.actions";
import {
    CollectiveChallengeSubscription,
    CollectiveChallengeUnSubscription,
    EChallengesActions,
    IndividualChallengeSubscription,
    LoadChallenges,
    LoadEvent,
    LoadOtherEvents,
    ContentChallengeSubscription
} from "@edp-store/actions/challenges.actions";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { IAppState } from "@edp-store/state/app.state";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, mergeMap, switchMap } from "rxjs/operators";
import { EEdpeActions } from "@edp-store/actions/edpe.actions";
import { ELevelsActions } from "@edp-store/actions/levels.actions";

@Injectable()
export class ChallengeEffects {
    loadChallengesLevels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.LoadChallengesLevels),
            switchMap(() => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.getChallengesLevels().pipe(
                    switchMap(response => [
                        { type: EChallengesActions.SaveChallengesLevels, payload: response },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ]),
                    catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
                );
            })
        )
    );

    loadEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.LoadEvent),
            switchMap((action: LoadEvent) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.getEvent(action.eventType, action.challengeId, action.eventCode).pipe(
                    switchMap(response => [
                        {
                            type:
                                action.eventType === "COLLECTIVE_CHALLENGE"
                                    ? EChallengesActions.SaveCollectiveChallenge
                                    : EChallengesActions.SaveExperiences,
                            payload: response,
                        },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ]),
                    catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
                );
            })
        )
    );

    loadOtherEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.LoadOtherEvents),
            switchMap((action: LoadOtherEvents) =>
                this.challengesService.getEventsList(action.eventCode, action.eventType, action.limit).pipe(
                    switchMap((response: ICard[]) => [
                        {
                            type: EChallengesActions.SaveOtherEvents,
                            payload: response.map(card => BenefitsFactory.create(card)),
                        },
                    ]),
                    catchError(() => [
                        {
                            type: EChallengesActions.SaveOtherEvents,
                            payload: [],
                        },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ])
                )
            )
        )
    );

    individualChallengeSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.IndividualChallengeSubscription),
            switchMap((args: IndividualChallengeSubscription) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.individualChallengeSubscription(args.payload).pipe(
                    switchMap(() => {
                        this.googleAnalyticsService.datalayerEvent("SUBSCRIPTION_INDIVIDUAL_CHALLENGE");
                        return [
                            { type: EChallengesActions.SaveChallengeAcceptance, payload: { status: true } },
                            { type: EChallengesActions.LoadChallengesLevels },
                            { type: EBenefitsActions.GetAvailable },
                            { type: EEdpeActions.GetDashboardSections },
                            { type: EEdpeActions.GetUserEfficiency },
                            { type: ELevelsActions.LoadUserLevel },
                            { type: ELayoutActions.ShowLoader, payload: false },
                        ];
                    }),
                    catchError((error: Error) =>
                        of(
                            {
                                type: EChallengesActions.SaveChallengeAcceptance,
                                payload: { status: false, errorCode: error.message },
                            },
                            { type: ELayoutActions.ShowLoader, payload: false }
                        )
                    )
                );
            })
        )
    );

    collectiveChallengeSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.CollectiveChallengeSubscription),
            switchMap((args: CollectiveChallengeSubscription) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.collectiveChallengeSubscription(args.payload).pipe(
                    switchMap(() => {
                        this.googleAnalyticsService.datalayerEvent(args.payload.type === "EXPERIENCE" ?
                            "SUBSCRIPTION_EXPERIENCE" : "SUBSCRIPTION_COLLECTIVE_CHALLENGE");
                        return [
                            { type: EChallengesActions.SaveChallengeAcceptance, payload: { status: true } },
                            { type: EBenefitsActions.GetAvailable },
                            { type: EEdpeActions.GetDashboardSections },
                            { type: ELayoutActions.ShowLoader, payload: false },
                        ];
                    }),
                    catchError(response =>
                        of(
                            {
                                type: EChallengesActions.SaveChallengeAcceptance,
                                payload: { status: true, errorCode: response },
                            },
                            { type: ELayoutActions.ShowLoader, payload: false }
                        )
                    )
                );
            })
        )
    );

    collectiveChallengeUnSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.CollectiveChallengeUnSubscription),
            switchMap((args: CollectiveChallengeUnSubscription) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.collectiveChallengeUnSubscription(args.payload).pipe(
                    switchMap(() => {
                        this.googleAnalyticsService.datalayerEvent(args.payload.type === "EXPERIENCE" ?
                            "SUBSCRIPTION_EXPERIENCE_CANCEL" : "SUBSCRIPTION_COLLECTIVE_CHALLENGE_CANCEL");
                        return [
                            { type: EChallengesActions.SaveChallengeAcceptance, payload: { status: true } },
                            { type: EBenefitsActions.GetAvailable },
                            { type: EEdpeActions.GetDashboardSections },
                            { type: ELayoutActions.ShowLoader, payload: false },
                        ];
                    }),
                    catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
                );
            })
        )
    );

    loadChallenges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.LoadChallenges),
            mergeMap((args: LoadChallenges) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.getEventsList(args.eventCode, args.eventType, null, args.state).pipe(
                    mergeMap((response: ICard[]) => [
                        {
                            type:
                                args.state === "ACTIVE"
                                    ? EChallengesActions.SaveActiveChallenges
                                    : EChallengesActions.SaveInactiveChallenges,
                            payload: response.map(card => BenefitsFactory.create(card)),
                        },
                        { type: ELayoutActions.ShowLoader, payload: false },
                    ]),
                    catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
                );
            })
        )
    );

    contentChallengeSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(EChallengesActions.ContentChallengeSubscription),
            switchMap((args: ContentChallengeSubscription) => {
                this.store.dispatch(new ShowLoader(true));
                return this.challengesService.contentChallengeSubscription(args.payload).pipe(
                    mergeMap(() => {
                        return [
                            { type: EEdpeActions.GetDashboardSections },
                            { type: ELayoutActions.ShowLoader, payload: false },
                            { type: ELevelsActions.LoadUserLevel, payload: { place: "DASHBOARD" } },
                        ];
                    }),
                    catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
                );
            }),
        )
    );

    constructor(
        private actions$: Actions,
        private challengesService: ChallengesService,
        private store: Store<IAppState>,
        private googleAnalyticsService: GoogleAnalyticsService) { }
}
